import { reportsApi } from './api/reportsApi';

import { StratusStatsReport } from './components/stratus-stats-report/stratus-stats-report';
import { ReportModel } from './models/report/ReportModel';
import { TemplateModel } from './models/template/TemplateModel';
import ReportType from './enums/report-type';
import { matchesApi } from './api/matchesApi';
import { MatchTeamModel } from './models/scoresummary/MatchTeamModel';
import { TeamModel } from './models/report/TeamModel';
import { MetricModel } from './models/report/MetricModel';
import ScoreTypes, { ScoreTypeMapping } from './enums/score-types';
import { MatchModel } from './models/report/MatchModel';
import { MatchMetaDataModel } from './models/report/meta-data/MatchMetaDataModel';
import { RowModel } from './models/template/RowModel';
import CardComponentType from './enums/card-component-type';
import { teamsApi } from './api/teamsApi';
import ReportSetup from './components/report-setup/report-setup';
import ReportBuilder from './components/report-builder/report-builder';
import Dropdown from "./components/dropdown/dropdown";
import ReportDashboard from './components/report-dashboard/report-dashboard';
import { UserModel } from './models/dashboard/UserModel';
import { DefaultReportConfigItemModel, DefaultReportConfigModel } from './models/report/DefaultReportConfigModel';
import { ReportConfigModel } from './models/report/ReportConfigModel';
import Consts from './consts/consts';
import DefaultReportConfig from './components/default-report-config/default-report-config';
import PlayerCareerReport from './components/player-career-report/player-career-report';
import BroadcastLiveStats from './components/broadcast-live-stats/broadcast-live-stats';
import PassageReport from './components/passage-report/passage-report';

class StratusStatistics {
    private _reportsApi: reportsApi;
    private _matchesApi: matchesApi;
    private _teamsApi: teamsApi;
    private _playlistClickThrough: boolean;
    private _isMultiTemplate: boolean;

    constructor() {

    }

    initBuildReport(reportContainer: HTMLElement, baseUrl: string, userId: string) {
        reportContainer.appendChild(new ReportBuilder(reportContainer, baseUrl, userId));
    }

    async initSetupReport(setupContainer: HTMLElement, reportContainer: HTMLElement, mediaUrl: string, portalUrl: string, userId: string) {
        setupContainer.appendChild(new ReportSetup(reportContainer, mediaUrl, portalUrl, userId, this.initReport.bind(this.initReport)));
    }

    async initDashboard(setupContainer: HTMLElement, reportContainer: HTMLElement, mediaUrl: string, reportsUrl: string, portalUrl: string, userId: string, userIds: UserModel[]) {
        setupContainer.appendChild(new ReportDashboard(reportContainer, mediaUrl, reportsUrl, portalUrl, userId, userIds, this.initReport.bind(this.initReport)));
    }

    async initDefaultReportConfig(setupContainer: HTMLElement, reportContainer: HTMLElement, mediaUrl: string, reportsUrl: string, userId: string, userIds: UserModel[], reportType: number) {
        setupContainer.appendChild(new DefaultReportConfig(reportContainer, mediaUrl, reportsUrl, userId, userIds, reportType));
    }

    async initPlayerCareerReport(setupContainer: HTMLElement, mediaUrl: string, userId: string) {
        setupContainer.appendChild(new PlayerCareerReport(mediaUrl, userId));
    }

    async initBroadcastLiveStats(setupContainer: HTMLElement, matchId: string, performanceMediaUrl: string, reportsUrl: string, userId: string, showPlayers: boolean, homeTeamColour: string, awayTeamColour: string) {
        setupContainer.appendChild(new BroadcastLiveStats(matchId, performanceMediaUrl, reportsUrl, userId, showPlayers, homeTeamColour, awayTeamColour));
    }

    async initPassageReport(setupContainer: HTMLElement, matchId: string, performanceMediaUrl: string, userId: string) {
        setupContainer.appendChild(new PassageReport(matchId, performanceMediaUrl, userId));
    }

    async initReport(baseUrl: string,
        userId: string,
        container: HTMLElement,
        reportId: string,
        matchId: string,
        templateId: string,
        loader: any,
        extraStyles: string,
        reportType: number,
        logoTop: boolean,
        showTeams: boolean,
        seasonId: string,
        showPlayerStats: boolean,
        playerTemplateId: string,
        playlistClickThrough: boolean,
        seriesId: string,
        reportFilterId: string,
        isMultiTemplate: boolean) {

        this._reportsApi = new reportsApi(baseUrl, userId);
        this._matchesApi = new matchesApi(baseUrl, userId);
        this._teamsApi = new teamsApi(baseUrl, userId);

        this._playlistClickThrough = playlistClickThrough;
        this._isMultiTemplate = isMultiTemplate;

        if (loader)
            container.appendChild(loader);
        else
            container.innerHTML = "<div>fetching stats</div>";

        let report: ReportModel = null;
        let template: TemplateModel = null;
        let playerTemplate: TemplateModel = null;
        let playerReport: ReportModel = null;
        let teamsFormatch;

        //Local functions
        let templateHasPlayerMetricsFunc = function (rows: RowModel[]) {
            let hasPlayerMetrics = false;

            for (let [i, row] of Object.entries(rows)) {
                for (let [j, column] of Object.entries(row.columns)) {
                    if (column.components && column.components.length > 0) {
                        for (let [k, component] of Object.entries(column.components)) {
                            if (component.type == CardComponentType.PlayerBreakdownTable) {
                                hasPlayerMetrics = true;
                                break;
                            }
                        }
                    } else {
                        if (templateHasPlayerMetricsFunc(column.rows)) {
                            hasPlayerMetrics = true;
                            break;
                        }
                    }
                }

                if (hasPlayerMetrics)
                    break;
            }

            return hasPlayerMetrics;
        };

        // If a view match report is requested, try and identify user's default configured ViewMatch Report. Else, use the default
        if (reportType === ReportType.ViewMatchReport) {
            let hasDefaultReport = false;

            let defaultReportConfig: DefaultReportConfigModel = await this._reportsApi.GetDefaultReportConfigFromMedia(userId);

            if (defaultReportConfig && defaultReportConfig?.configItems) {
                let defaultViewMatchReport: DefaultReportConfigItemModel = defaultReportConfig.configItems.find(x => x.reportType === ReportType.ViewMatchReport);

                if (defaultViewMatchReport && defaultViewMatchReport?.templateId) {
                    let defaultTemplate: TemplateModel = await this._reportsApi.GetTemplateFromMedia(defaultViewMatchReport.userId ?? userId, defaultViewMatchReport.templateId);

                    if (defaultTemplate) {
                        hasDefaultReport = true;
                        this._isMultiTemplate = defaultTemplate.subTemplates.length > 0 ? true : false;
                        reportId = defaultTemplate.reportId;
                        templateId = defaultTemplate.reportTemplateId;
                        reportType = defaultTemplate.reportType;
                        userId = defaultViewMatchReport.userId;
                    }
                }
            }

            if (!hasDefaultReport) {
                reportType = ReportType.MatchReport;
                userId = Consts.PrimaryUserId;
            }

            this._reportsApi = new reportsApi(baseUrl, userId);
            this._matchesApi = new matchesApi(baseUrl, userId);
            this._teamsApi = new teamsApi(baseUrl, userId);
        }

        if (this._isMultiTemplate) {

            container.innerHTML = '';
            let loadingContainer = document.createElement('div');
            loadingContainer.setAttribute("id", `loading-container`);
            container.appendChild(loadingContainer);

            if (loader)
                loadingContainer.appendChild(loader);
            else
                loadingContainer.innerHTML = "<div>fetching stats</div>";

            let parentTemplate: TemplateModel = await this._reportsApi.GetTemplateFromMedia(userId, templateId, true);

            parentTemplate.subTemplates.map((subTemplate, index) => {

                let newChildContainer = document.createElement('div');
                newChildContainer.setAttribute("style", "margin-bottom: 10px;");
                newChildContainer.setAttribute("id", `child-container-${subTemplate.reportTemplateId}`);
                container.appendChild(newChildContainer);
            });

            let reports: StratusStatsReport[] = [];

            await Promise.all(parentTemplate.subTemplates.map(async (subTemplate, index) => {

                let subReport: ReportModel = null;
                let subReportTemplate: TemplateModel = null;

                switch (subTemplate.reportType) {
                    case ReportType.TeamMatchReport:
                        [subReport, subReportTemplate] = await Promise.all([
                            this._reportsApi.GetTeamMatchReport(subTemplate.reportId, matchId, "", reportFilterId),
                            this._reportsApi.GetTemplateFromMedia(userId, subTemplate.reportTemplateId)
                        ]);
                        break;
                    case ReportType.PlayerMatchReport:
                        [subReport, subReportTemplate] = await Promise.all([
                            this._reportsApi.GetPlayerMatchReport(subTemplate.reportId, matchId, "", "", reportFilterId),
                            this._reportsApi.GetTemplateFromMedia(userId, subTemplate.reportTemplateId)
                        ]);
                        break;
                    case ReportType.TeamSeasonReport:
                        [subReport, subReportTemplate] = await Promise.all([
                            this._reportsApi.GetTeamSeasonReport(subTemplate.reportId, seasonId, "", reportFilterId),
                            this._reportsApi.GetTemplateFromMedia(userId, subTemplate.reportTemplateId)
                        ]);
                        break;
                    case ReportType.PlayerSeasonReport:
                        [subReport, subReportTemplate] = await Promise.all([
                            this._reportsApi.GetPlayerSeasonReport(subTemplate.reportId, seasonId, "", "", reportFilterId),
                            this._reportsApi.GetTemplateFromMedia(userId, subTemplate.reportTemplateId)
                        ]);
                        break;
                    case ReportType.TeamSeriesReport:
                        [subReport, subReportTemplate] = await Promise.all([
                            this._reportsApi.GetTeamSeriesReport(subTemplate.reportId, seriesId, "", reportFilterId),
                            this._reportsApi.GetTemplateFromMedia(userId, subTemplate.reportTemplateId)
                        ]);
                        break;
                    case ReportType.PlayerSeriesReport:
                        [subReport, subReportTemplate] = await Promise.all([
                            this._reportsApi.GetPlayerSeriesReport(subTemplate.reportId, seriesId, "", "", reportFilterId),
                            this._reportsApi.GetTemplateFromMedia(userId, subTemplate.reportTemplateId)
                        ]);
                        break;
                    case ReportType.MatchReport:
                        [subReport, subReportTemplate] = await Promise.all([
                            this._reportsApi.GetMatchReport(subTemplate.reportId, matchId, reportFilterId),
                            this._reportsApi.GetTemplateFromMedia(userId, subTemplate.reportTemplateId)
                        ]);
                        break;
                    case ReportType.SeasonReport:
                        [subReport, subReportTemplate] = await Promise.all([
                            this._reportsApi.GetSeasonReport(subTemplate.reportId, seasonId, reportFilterId),
                            this._reportsApi.GetTemplateFromMedia(userId, subTemplate.reportTemplateId)
                        ]);
                        break;
                    case ReportType.SeriesReport:
                        [subReport, subReportTemplate] = await Promise.all([
                            this._reportsApi.GetSeriesReport(subTemplate.reportId, seriesId, reportFilterId),
                            this._reportsApi.GetTemplateFromMedia(userId, subTemplate.reportTemplateId)
                        ]);
                        break;
                }

                // Get Team Header if necessary
                let showTeamHeader = showTeams && index === 0;

                if (showTeamHeader) {
                    let teamsForMatch = await this._teamsApi.GetTeamsForMatch(matchId);

                    if (teamsForMatch) {
                        for (let [i, teamForMatch] of Object.entries(teamsForMatch)) {
                            let team = subReport.teams.find((team) => team.teamId == teamForMatch["teamGuid"]);
                            team.score = teamForMatch["matchTeam"]["score"];
                        }
                    }
                }

                // Override Colors
                subReportTemplate.baseColor = parentTemplate.baseColor;
                subReportTemplate.secondaryColor = parentTemplate.secondaryColor;
                subReportTemplate.awayTeam.color = parentTemplate.awayTeam?.color;
                subReportTemplate.homeTeam.color = parentTemplate.homeTeam?.color;
                let statsReport = new StratusStatsReport(matchId, seasonId, subReportTemplate, subReport, extraStyles, logoTop, showTeamHeader, showPlayerStats, subTemplate.reportType, null, this._reportsApi, this._playlistClickThrough, userId, subTemplate.reportId);

                reports.push(statsReport);
            }));

            reports.map((statsReport, index) => {

                let childContainer = document.getElementById(`child-container-${statsReport._template.reportTemplateId}`);
                childContainer.appendChild(statsReport);
            });

            loadingContainer.innerHTML = "";
        }
        else {
            switch (reportType) {
                case ReportType.TeamMatchReport:
                    let apiCalls = [this._reportsApi.GetTeamMatchReport(reportId, matchId, "", reportFilterId),
                    this._reportsApi.GetTemplateFromMedia(userId, templateId),
                    (playerTemplateId ? this._reportsApi.GetTemplateFromMedia(userId, playerTemplateId) : Promise.resolve()),
                    (playerTemplateId ? this._reportsApi.GetPlayerMatchReport(reportId, matchId, "", "", reportFilterId) : Promise.resolve())];

                    if (showTeams)
                        apiCalls.push(this._teamsApi.GetTeamsForMatch(matchId));
                    else
                        apiCalls.push(Promise.resolve());

                    [report, template, playerTemplate, playerReport, teamsFormatch] = await Promise.all(apiCalls);

                    if (templateHasPlayerMetricsFunc(template.rows)) {
                        let matchPlayerReport = await this._reportsApi.GetPlayerMatchReport(reportId, matchId, '', '', reportFilterId);
                        report.players = matchPlayerReport.players;
                    }

                    if (teamsFormatch) {
                        for (let [i, teamForMatch] of Object.entries(teamsFormatch)) {
                            let team = report.teams.find((team) => team.teamId == teamForMatch["teamGuid"]);
                            team.score = teamForMatch["matchTeam"]["score"];
                        }
                    }

                    break;
                case ReportType.PlayerMatchReport:
                    [report, template] = await Promise.all([
                        this._reportsApi.GetPlayerMatchReport(reportId, matchId, "", "", reportFilterId),
                        this._reportsApi.GetTemplateFromMedia(userId, templateId)
                    ]);

                    if (showTeams) {
                        let teamsForMatch = await this._teamsApi.GetTeamsForMatch(matchId);

                        if (teamsForMatch) {
                            for (let [i, teamForMatch] of Object.entries(teamsForMatch)) {
                                let team = report.teams.find((team) => team.teamId == teamForMatch["teamGuid"]);
                                team.score = teamForMatch["matchTeam"]["score"];
                            }
                        }
                    }

                    break;
                case ReportType.TeamSeasonReport:
                    [report, template] = await Promise.all([
                        this._reportsApi.GetTeamSeasonReport(reportId, seasonId, "", reportFilterId),
                        this._reportsApi.GetTemplateFromMedia(userId, templateId)
                    ]);
                    break;
                case ReportType.PlayerSeasonReport:
                    [report, template] = await Promise.all([
                        this._reportsApi.GetPlayerSeasonReport(reportId, seasonId, "", "", reportFilterId),
                        this._reportsApi.GetTemplateFromMedia(userId, templateId)
                    ]);
                    break;

                case ReportType.TeamSeriesReport:
                    [report, template] = await Promise.all([
                        this._reportsApi.GetTeamSeriesReport(reportId, seriesId, "", reportFilterId),
                        this._reportsApi.GetTemplateFromMedia(userId, templateId)
                    ]);
                    break;
                case ReportType.PlayerSeriesReport:
                    [report, template] = await Promise.all([
                        this._reportsApi.GetPlayerSeriesReport(reportId, seriesId, "", "", reportFilterId),
                        this._reportsApi.GetTemplateFromMedia(userId, templateId)
                    ]);
                    break;
                case ReportType.TeamMatchScoreSummary:
                    let matchTeams: MatchTeamModel[];

                    [matchTeams, template] = await Promise.all([
                        this._matchesApi.GetScoreSummaryForMatch(matchId),
                        this._reportsApi.GetTemplateFromMedia(userId, templateId)
                    ]);

                    let awayTeam = matchTeams.find(matchTeam => matchTeam.isPrimary == false);
                    let homeTeam = matchTeams.find(matchTeam => matchTeam.isPrimary == true);

                    report = <ReportModel>{
                        teams: [],
                        matches: [<MatchModel>{
                            matchId: matchId,
                            matchMetaData: <MatchMetaDataModel>{
                                awayTeamID: awayTeam.teamGuid,
                                homeTeamID: homeTeam.teamGuid
                            }
                        }]
                    };

                    for (let [i, matchTeam] of Object.entries(matchTeams)) {
                        let teamModel: TeamModel = <TeamModel>{
                            teamId: matchTeam.teamGuid,
                            metrics: [],
                            teamMetaData: {}
                        };

                        for (let [j, scoreSummary] of Object.entries(matchTeam.matchScoreSummary)) {
                            let scoreTypeMapping: ScoreTypeMapping = ScoreTypes.getScoreType(scoreSummary.scoreTypeId);

                            teamModel.metrics.push(<MetricModel>{
                                metricId: scoreTypeMapping.metricId.toString(),
                                value: scoreSummary.count ?? 0,
                                itemId: teamModel.teamId
                            });
                        }

                        report.teams.push(teamModel);
                    }
                    break;
                case ReportType.MatchReport:
                    [report, template] = await Promise.all([
                        this._reportsApi.GetMatchReport(reportId, matchId, reportFilterId),
                        this._reportsApi.GetTemplateFromMedia(userId, templateId)
                    ]);
                    break;
                case ReportType.SeasonReport:
                    [report, template] = await Promise.all([
                        this._reportsApi.GetSeasonReport(reportId, seasonId, reportFilterId),
                        this._reportsApi.GetTemplateFromMedia(userId, templateId)
                    ]);
                    break;
                case ReportType.SeriesReport:
                    [report, template] = await Promise.all([
                        this._reportsApi.GetSeriesReport(reportId, seriesId, reportFilterId),
                        this._reportsApi.GetTemplateFromMedia(userId, templateId)
                    ]);
                    break;
            }

            container.innerHTML = '';

            let playerStatsReport;
            if (playerReport && playerTemplateId) {
                playerStatsReport = new StratusStatsReport(matchId, seasonId, playerTemplate, playerReport, extraStyles, logoTop, false, false, reportType, null, this._reportsApi, this._playlistClickThrough, userId, playerReport.reportId);
            }

            let statsReport = new StratusStatsReport(matchId, seasonId, template, report, extraStyles, logoTop, showTeams, showPlayerStats, reportType, playerStatsReport, this._reportsApi, this._playlistClickThrough, userId, report.reportId);

            container.appendChild(statsReport);
        }
    }
}

const stratusStatistics = new StratusStatistics();

export { stratusStatistics as instance }

window.customElements.define('index-stratus-stats-dropdown', Dropdown as any);